<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <div class="flex justify-center mb-6">
          <InvestBuddyLogo :show-tagline="true" />
        </div>
        <h2 class="text-3xl font-extrabold text-gray-900">
          {{ showRegister ? 'Create your account' : 'Sign in to your account' }}
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          AI-powered portfolio analysis, real-time market insights, and personalized investment recommendations
        </p>
      </div>

      <!-- Success message for registration -->
      <div v-if="registrationSuccess" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-green-800">Registration successful!</h3>
            <p class="mt-1 text-sm text-green-700">Please check your email to verify your account before signing in.</p>
          </div>
        </div>
      </div>
      
      <!-- Login Form -->
      <form v-if="!showRegister" class="mt-8 space-y-6" @submit.prevent="handleLogin">
        <div class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email"
            >
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div class="relative mt-1">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              >
              <button
                type="button"
                @click="toggleShowPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500 hover:text-gray-700"
                :aria-pressed="showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
              >
                {{ showPassword ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div class="mt-2 text-sm text-right">
              <button type="button" @click="goToForgotPassword" class="text-blue-600 hover:text-blue-500">Forgot password?</button>
            </div>
          </div>
        </div>

        <div v-if="authStore.error" class="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3">
          {{ authStore.error }}
        </div>

        <div>
          <button
            type="submit"
            :disabled="authStore.loading"
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {{ authStore.loading ? 'Signing in...' : 'Sign in' }}
          </button>
        </div>

        <!-- Divider -->
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-gray-50 text-gray-500">Or continue with</span>
          </div>
        </div>

        <!-- Google Sign In -->
        <div>
          <button
            type="button"
            @click="handleGoogleLogin"
            class="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>

        <div class="text-center">
          <RouterLink
            to="/register"
            class="text-blue-600 hover:text-blue-500 text-sm"
          >
            Don't have an account? Sign up
          </RouterLink>
        </div>
      </form>

      <!-- Registration Form -->
      <form v-if="showRegister" class="mt-6 space-y-4" @submit.prevent="handleRegister">
        <div class="space-y-4">
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              id="name"
              v-model="name"
              type="text"
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your full name"
            >
          </div>

          <div>
            <label for="reg-email" class="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="reg-email"
              v-model="regEmail"
              type="email"
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email"
            >
          </div>

          <div>
            <label for="reg-password" class="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="reg-password"
              v-model="regPassword"
              type="password"
              required
              minlength="8"
              class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Create a password (min 8 characters)"
            >
            <p class="mt-1 text-xs text-gray-500">Must be at least 8 characters long</p>
          </div>
        </div>

        <div v-if="authStore.error" class="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3">
          {{ authStore.error }}
        </div>

        <div>
          <button
            type="submit"
            :disabled="authStore.loading"
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {{ authStore.loading ? 'Creating account...' : 'Create Account' }}
          </button>
        </div>

        <!-- Divider -->
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-gray-50 text-gray-500">Or sign up with</span>
          </div>
        </div>

        <!-- Google Sign Up -->
        <div>
          <button
            type="button"
            @click="handleGoogleLogin"
            class="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </button>
        </div>

        <div class="text-center">
          <button
            type="button"
            @click="toggleForm"
            class="text-blue-600 hover:text-blue-500 text-sm"
          >
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import InvestBuddyLogo from '@/components/InvestBuddyLogo.vue'
import { apiUrl } from '../config/api'

const router = useRouter()
const authStore = useAuthStore()

// Login form
const email = ref('')
const password = ref('')
const showPassword = ref(false)

function toggleShowPassword() {
  showPassword.value = !showPassword.value
}

function goToForgotPassword() {
  router.push('/forgot-password')
}

// Registration form
const showRegister = ref(false)
const registrationSuccess = ref(false)
const name = ref('')
const regEmail = ref('')
const regPassword = ref('')

function toggleForm() {
  showRegister.value = !showRegister.value
  registrationSuccess.value = false
  authStore.error = null
}

async function handleLogin() {
  try {
    await authStore.login(email.value, password.value)
    router.push('/dashboard')
  } catch (error: any) {
    console.error('Login failed:', error)
    
    // Handle specific error codes
    const errorCode = error.response?.data?.code
    const errorMessage = error.response?.data?.error
    
    if (errorCode === 'EMAIL_NOT_VERIFIED') {
      authStore.error = errorMessage
      // User can check their email for verification link
    } else if (errorCode === 'PHONE_NOT_VERIFIED') {
      authStore.error = errorMessage
      // Optionally redirect to phone verification
      setTimeout(() => {
        router.push('/verify-phone')
      }, 3000)
    }
    // Error is already set in the store for other cases
  }
}

async function handleRegister() {
  try {
    // Call register API
    const response = await fetch(apiUrl('/api/auth/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: regEmail.value,
        password: regPassword.value,
        name: name.value
      })
    })

    const data = await response.json()

    if (!response.ok) {
      authStore.error = data.error || 'Registration failed'
      return
    }

    // Show success message
    registrationSuccess.value = true
    showRegister.value = false
    
    // Clear form
    name.value = ''
    regEmail.value = ''
    regPassword.value = ''
    authStore.error = null
  } catch (error: any) {
    console.error('Registration failed:', error)
    authStore.error = 'Registration failed. Please try again.'
  }
}

function handleGoogleLogin() {
  // Redirect to backend Google OAuth route
  window.location.href = apiUrl('/api/auth/google')
}
</script>
